import { IStudySystemLogTableParams } from '@my-backoffice/app/pages/life-sciences/shared/interfaces/study-system-log-table-params.interface';
import { LifeSciencesStudySystemLogService } from '@my-backoffice/app/pages/life-sciences/shared/services/life-sciences-study-system-log.service';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const SYSTEM_LOG_LIST_PATH = '/life-science-service/v1.0/study/';

@Injectable()
export class LifeSciencesStudyMockInterceptor implements HttpInterceptor {
  private readonly _lifeSciencesStudySystemLogService = inject(LifeSciencesStudySystemLogService);

  /**
   * @description
   * Intercepts life-science system log list requests and returns mock paginated data.
   * Study list search uses the real `/v1.0/sso/study/search` API (not mocked here).
   * @param {Readonly<HttpRequest<unknown>>} request - Outgoing HTTP request.
   * @param {Readonly<HttpHandler>} next - Next handler in the interceptor chain.
   * @returns {Observable<HttpEvent<unknown>>} Mock response or forwarded request.
   */
  public intercept<T>(request: Readonly<HttpRequest<T>>, next: Readonly<HttpHandler>): Observable<HttpEvent<T>> {
    if (!isDevMode()) {
      return next.handle(request);
    }

    if (this._isSystemLogListRequest(request.url)) {
      return this._handleSystemLogList$<T>(request.url);
    }

    return next.handle(request);
  }

  private _handleSystemLogList$<T>(url: string): Observable<HttpEvent<T>> {
    const tableParams = this._parseSystemLogTableParams(url);

    return this._lifeSciencesStudySystemLogService.getSystemLogsPage$(tableParams).pipe(
      switchMap((page): Observable<HttpEvent<T>> => {
        return of(this._buildPageResponse<T>(page));
      })
    );
  }

  private _buildPageResponse<T>(page: { pageItems: unknown[]; pageNo: number; totalPageNum: number; totalSize: number }): HttpResponse<T> {
    return new HttpResponse({
      status: 200,
      body: {
        pageItems: page.pageItems,
        pageNo: page.pageNo.toString(),
        totalPageNum: page.totalPageNum.toString(),
        totalSize: page.totalSize,
      } as T,
    });
  }

  private _isSystemLogListRequest(url: string): boolean {
    return url.includes(SYSTEM_LOG_LIST_PATH) && url.includes('/system-logs');
  }

  private _parseSystemLogTableParams(url: string): IStudySystemLogTableParams {
    const parsedUrl = new URL(url, 'http://localhost');
    const pathSegments = parsedUrl.pathname.split('/');
    const studyIndex = pathSegments.indexOf('study');
    const studyId = studyIndex >= 0 ? (pathSegments[studyIndex + 1] ?? '') : '';

    return {
      studyId,
      pageNo: parseInt(parsedUrl.searchParams.get('pageNo') ?? '1', 10),
      pageSize: parseInt(parsedUrl.searchParams.get('pageSize') ?? '10', 10),
    };
  }
}
